/**
 * Turn a pyseowind write response (or a thrown error) into a clear failure, so
 * write thunks can `rejectWithValue` instead of resolving with `undefined`.
 *
 * Why: the save chain treats a fulfilled thunk as "saved". When a thunk caught a
 * 401/403 `PyApiError` (or a non-ok response) and returned nothing, the editor
 * marked the refused body as saved locally. Kept free of imports so the node
 * test runner can load it directly.
 */

export type PyWriteError = {
  status?: number;
  code?: string;
  message: string;
};

const AUTH_MESSAGES: Record<number, string> = {
  401: "Your session has expired. Sign in again — this change was not saved.",
  403: "You don't have permission to change this article — this change was not saved.",
};

export const isVersionConflict = (json: any): boolean =>
  Number(json?.status_code) === 409 || json?.data?.code === "VERSION_CONFLICT";

/**
 * Failure carried by a parsed write response, or `null` when it succeeded.
 * A 409 version conflict is NOT a failure here: the editor's save path has its
 * own reload-HEAD handling for it and needs the body.
 */
export const writeFailureFromResponse = (
  httpStatus: number,
  json: any,
  fallback: string
): PyWriteError | null => {
  if (isVersionConflict(json)) return null;
  const bodyStatus = Number(json?.status_code);
  const status =
    httpStatus >= 400 ? httpStatus : Number.isFinite(bodyStatus) && bodyStatus >= 400 ? bodyStatus : 0;
  if (!status && json != null) return null;
  const finalStatus = status || httpStatus;
  return {
    status: finalStatus,
    code: json?.data?.code,
    message: AUTH_MESSAGES[finalStatus] || json?.data?.message || fallback,
  };
};

/** Map a thrown error (`PyApiError` for 401/403, or a network error) to a reject value. */
export const toPyWriteError = (err: any, fallback: string): PyWriteError => {
  const status = typeof err?.status === "number" ? err.status : undefined;
  return {
    status,
    code: err?.body?.data?.code,
    message: (status && AUTH_MESSAGES[status]) || fallback,
  };
};

/** User-facing message for anything a write path threw or rejected with. */
export const describeWriteError = (err: any, fallback: string): string => {
  const status = typeof err?.status === "number" ? err.status : undefined;
  return (status && AUTH_MESSAGES[status]) || err?.message || fallback;
};
